/**
 * Settings — live platform limits, security flags, notifications,
 * maintenance mode and feature flags.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, errorMessage } from "../lib/admin";
import { adminApi } from "../lib/api";
import type { SettingsPayload } from "../lib/types";
import { Gauge, Shield, Bell, Palette, AlertTriangle } from "lucide-react";

interface Flag {
  name: string;
  enabled: boolean;
}

type BoolSections = "security" | "notifications" | "maintenance";

const SECTION_META: Array<{ key: BoolSections; title: string; icon: React.ReactNode; labels: Record<string, string> }> = [
  {
    key: "security",
    title: "Security",
    icon: <Shield size={18} />,
    labels: { mfa_enforced: "Enforce MFA", session_lock: "Session lock", device_binding: "Device binding" },
  },
  {
    key: "notifications",
    title: "Notifications",
    icon: <Bell size={18} />,
    labels: { alert_unknown_orders: "Alert unknown orders", daily_recon: "Daily reconciliation", funding_mismatch: "Funding mismatch alerts" },
  },
  {
    key: "maintenance",
    title: "Maintenance",
    icon: <Palette size={18} />,
    labels: { maintenance_mode: "Maintenance mode", force_version_update: "Force version update" },
  },
];

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      role="switch"
      aria-checked={on}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: on ? "var(--ev-blue-600)" : "var(--ev-slate-300)",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.2s",
        marginTop: 2,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 10,
          background: "white",
          position: "absolute",
          top: 2,
          left: on ? 22 : 2,
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        }}
      />
    </div>
  );
}

export function Settings() {
  const queryClient = useQueryClient();
  const settings = useAdminQuery<SettingsPayload>(["settings"], "/admin/v1/settings");
  const flags = useAdminQuery<{ flags: Flag[] }>(["feature-flags"], "/admin/v1/feature-flags");
  const [draft, setDraft] = useState<SettingsPayload | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (payload: SettingsPayload) => adminApi.patch("/admin/v1/settings", payload),
    onSuccess: () => {
      setFeedback("Settings saved");
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (error) => { setFeedback(null); setActionError(errorMessage(error)); },
  });

  const toggleFlag = useMutation({
    mutationFn: (flag: Flag) =>
      adminApi.post("/admin/v1/feature-flags", undefined, {
        query: { name: flag.name, enabled: !flag.enabled },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const updateLimit = (key: keyof SettingsPayload["limits"], majorValue: string) => {
    if (!draft) return;
    setDraft({
      ...draft,
      limits: { ...draft.limits, [key]: Math.round(Number(majorValue || 0) * 100) },
    });
  };

  const toggleSection = (section: BoolSections, key: string) => {
    if (!draft) return;
    const values = draft[section] as Record<string, boolean>;
    setDraft({ ...draft, [section]: { ...values, [key]: !values[key] } } as SettingsPayload);
  };

  return (
    <>
      <PageHead
        title="Settings"
        description="Limits, feature flags, maintenance mode, and version gate."
        actions={
          <button
            className="ev-btn ev-btn-sm ev-btn-primary"
            disabled={!draft || save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            Save changes
          </button>
        }
      />

      {(feedback || actionError) && (
        <div className={actionError ? "admin-note" : ""} style={{ marginBottom: 16 }}>
          {actionError ? (
            <div className="admin-note">
              <AlertTriangle size={16} />
              <span>{actionError}</span>
            </div>
          ) : (
            <span className="ev-body-micro" style={{ color: "var(--ev-mint-600)" }}>{feedback}</span>
          )}
        </div>
      )}

      {settings.isLoading && <LoadingCard label="Loading settings…" />}
      {settings.error && <ErrorCard error={settings.error} onRetry={() => void settings.refetch()} />}

      {draft && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div className="admin-card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: "var(--ev-radius-md)", background: "var(--ev-blue-100)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ev-blue-600)" }}>
                <Gauge size={18} />
              </div>
              <h3>Limits</h3>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
              {([
                ["daily_sale_min_minor", "Daily minimum (TZS)"],
                ["daily_sale_max_minor", "Daily maximum (TZS)"],
                ["txn_max_minor", "Per-transaction maximum (TZS)"],
              ] as const).map(([key, label]) => (
                <label key={key}>
                  <div className="ev-body-micro muted" style={{ marginBottom: 6 }}>{label}</div>
                  <input
                    className="ev-input"
                    type="number"
                    min="0"
                    value={draft.limits[key] / 100}
                    onChange={(e) => updateLimit(key, e.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>
              ))}
            </div>
          </div>

          {SECTION_META.map((section) => (
            <div key={section.key} className="admin-card" style={{ padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ width: 36, height: 36, borderRadius: "var(--ev-radius-md)", background: "var(--ev-blue-100)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ev-blue-600)" }}>
                  {section.icon}
                </div>
                <h3>{section.title}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {Object.entries(section.labels).map(([key, label]) => {
                  const values = draft[section.key] as Record<string, boolean>;
                  const on = Boolean(values[key]);
                  return (
                    <label
                      key={key}
                      style={{ display: "flex", gap: 16, alignItems: "flex-start", paddingBottom: 16, borderBottom: "1px solid var(--ev-border)" }}
                    >
                      <Toggle on={on} onClick={() => toggleSection(section.key, key)} />
                      <div>
                        <div className="ev-body-semibold">{label}</div>
                        <div className="ev-body-micro muted" style={{ marginTop: 2 }}>
                          {on ? "Enabled" : "Disabled"} · applies platform-wide
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="admin-card" style={{ padding: 24 }}>
            <h3>Feature flags</h3>
            {flags.isLoading && <div style={{ marginTop: 12 }}><LoadingCard label="Loading flags…" /></div>}
            {flags.error && <div style={{ marginTop: 12 }}><ErrorCard error={flags.error} /></div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
              {(flags.data?.flags ?? []).map((flag) => (
                <label key={flag.name} style={{ display: "flex", gap: 16, alignItems: "center", paddingBottom: 16, borderBottom: "1px solid var(--ev-border)" }}>
                  <Toggle on={flag.enabled} onClick={() => toggleFlag.mutate(flag)} />
                  <div>
                    <div className="ev-body-semibold">{flag.name.replaceAll("_", " ")}</div>
                    <div className="ev-body-micro muted">{flag.enabled ? "On" : "Off"}</div>
                  </div>
                </label>
              ))}
              {flags.data && flags.data.flags.length === 0 && (
                <span className="ev-body-micro muted">No feature flags defined.</span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
